import { IconMenu2, IconMoon, IconSun } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useContext } from "react";
import {
	ThemeContext,
	type ThemeContextInterface,
} from "@/components/providers/theme-provider";
import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";

export default function Header() {
	const navigation = config.Header.Links;
	const { darkTheme, toggleTheme } = useContext(
		ThemeContext,
	) as ThemeContextInterface;

	return (
		<header className="sticky top-0 z-50 border-b border-border/80 bg-background/75 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
			<nav
				aria-label="Global"
				className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-4 py-3 lg:px-8"
			>
				<div className="flex shrink-0">
					<Link
						to="/"
						aria-label="Homepage"
						className="group flex items-center gap-2 rounded-lg px-1 py-0.5 focus-visible:outline-offset-4"
					>
						<span className="size-2 rounded-full bg-primary shadow-[0_0_12px_color-mix(in_oklch,var(--primary)_45%,transparent)]" />
						<span className="text-base font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
							{config.Name}
						</span>
					</Link>
				</div>
				<div className="hidden items-center gap-1 lg:flex">
					{navigation.map((item) => (
						<Link
							key={item.name}
							to={item.href}
							className={cn(
								"rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors",
								"hover:bg-muted/80 hover:text-foreground",
								"[&.active]:bg-foreground/[0.06] [&.active]:text-foreground dark:[&.active]:bg-white/[0.08]",
							)}
						>
							{item.name}
						</Link>
					))}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="ml-1 text-muted-foreground hover:bg-muted/80 hover:text-foreground"
						onClick={toggleTheme}
						aria-label={
							darkTheme ? "Switch to light theme" : "Switch to dark theme"
						}
					>
						{darkTheme ? (
							<IconSun className="size-[1.125rem]" />
						) : (
							<IconMoon className="size-[1.125rem]" />
						)}
					</Button>
				</div>
				<div className="flex items-center gap-2 lg:hidden">
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="text-muted-foreground"
						onClick={toggleTheme}
						aria-label={
							darkTheme ? "Switch to light theme" : "Switch to dark theme"
						}
					>
						{darkTheme ? (
							<IconSun className="size-[1.125rem]" />
						) : (
							<IconMoon className="size-[1.125rem]" />
						)}
					</Button>
					<Sheet>
						<SheetTrigger asChild>
							<Button
								type="button"
								variant="outline"
								size="icon"
								className="shrink-0 border-border/80"
								aria-label="Open main menu"
							>
								<IconMenu2 className="size-5" />
							</Button>
						</SheetTrigger>
						<SheetContent
							side="right"
							className="w-[min(100%,20rem)] border-border/80"
						>
							<SheetHeader className="text-left">
								<SheetTitle className="font-semibold">Menu</SheetTitle>
							</SheetHeader>
							<div className="mt-8 flex flex-col gap-1">
								{navigation.map((item) => (
									<SheetClose key={item.name} asChild>
										<Link
											to={item.href}
											className={cn(
												"rounded-lg px-3 py-3 text-base font-medium text-foreground transition-colors",
												"hover:bg-muted/80",
												"[&.active]:bg-muted",
											)}
										>
											{item.name}
										</Link>
									</SheetClose>
								))}
							</div>
						</SheetContent>
					</Sheet>
				</div>
			</nav>
		</header>
	);
}
